import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  FaUserShield,
  FaFileAlt,
  FaCheck,
  FaTimes,
  FaExclamationCircle,
} from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import useAuth from "../../useAuth";
import { server } from "../../server";

export default function RoleForm({ role, onClose, onSuccess }) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      isActive: true,
    },
  });

  const isEdit = !!role;
  const { token } = useAuth();

  useEffect(() => {
    if (role) reset(role);
  }, [role, reset]);

  const onSubmit = async (data) => {
    try {
      const url = isEdit
        ? `${server}/roles/updateById?roleId=${role.id}`
        : `${server}/roles/new`;

      const method = isEdit ? "put" : "post";

      const payload = {
        ...data,
        isActive: data.isActive === true || data.isActive === "true",
      };

      await axios[method](url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success(isEdit ? "Role updated successfully!" : "Role created successfully!");
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error saving role.");
      console.error(error);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-800 overflow-hidden flex flex-col font-sans transition-colors duration-300">
      {/* Header */}
      <div className="flex justify-between items-center px-5 py-3 border-b border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800/80">
        <h3 className="text-sm font-black text-gray-900 dark:text-white flex items-center gap-2">
          <FaUserShield className="text-indigo-600 dark:text-indigo-400" />
          {isEdit ? "Edit System Role" : "Add New System Role"}
        </h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 text-xs font-bold transition"
        >
          <FaTimes />
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="p-5 space-y-3 text-xs">
        {/* Role Name */}
        <div>
          <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
            Role Name <span className="text-rose-500">*</span>
          </label>
          <div className="relative">
            <input
              {...register("name", { required: "Role name is required" })}
              className={`w-full p-2.5 bg-gray-50 dark:bg-slate-800 border ${
                errors.name
                  ? "border-rose-500 bg-rose-50 dark:bg-rose-950/30"
                  : "border-gray-200 dark:border-slate-700"
              } rounded-xl text-xs font-bold text-gray-900 dark:text-white outline-none transition`}
              placeholder="e.g. Administrator, Accountant, Librarian"
            />
            {errors.name && (
              <div className="absolute right-3 top-3 text-rose-500">
                <FaExclamationCircle size={12} />
              </div>
            )}
          </div>
          {errors.name && (
            <p className="text-[10px] text-rose-500 mt-1 font-semibold">
              {errors.name.message}
            </p>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
            Description / Scope
          </label>
          <div className="relative">
            <textarea
              {...register("description")}
              rows="3"
              className="w-full p-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-900 dark:text-white outline-none resize-none transition"
              placeholder="Describe the responsibilities and permissions of this role..."
            />
          </div>
        </div>

        {/* Active Checkbox */}
        <div className="flex items-center gap-2.5 p-2.5 bg-purple-50 dark:bg-purple-950/40 rounded-xl border border-purple-100 dark:border-purple-900/50">
          <input
            type="checkbox"
            id="isActive"
            {...register("isActive")}
            className="w-4 h-4 text-indigo-600 dark:text-indigo-400 rounded border-gray-300 dark:border-slate-700 focus:ring-indigo-500 cursor-pointer"
          />
          <label
            htmlFor="isActive"
            className="text-xs font-bold text-gray-700 dark:text-slate-200 cursor-pointer select-none"
          >
            Mark Role as Active
          </label>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-1.5 rounded-xl font-bold text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1.5 rounded-xl font-black transition shadow-xs flex items-center gap-1.5 disabled:opacity-50"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-1.5">
                <svg
                  className="animate-spin h-3.5 w-3.5 text-white"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Saving...
              </span>
            ) : (
              <>
                <FaCheck size={10} /> {isEdit ? "Update Role" : "Create Role"}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

